if (!customElements.get('product-slider')) {
  customElements.define(
    'product-slider',
    class ProductSlider extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.viewport = this.querySelector('.product-slider__viewport');
        this.track = this.querySelector('.product-slider__track');
        this.progressBar = this.querySelector('.product-slider__progress-thumb');
        this.prevBtn = this.querySelector('[data-slider-prev]');
        this.nextBtn = this.querySelector('[data-slider-next]');
        this.items = [...this.querySelectorAll('.product-slider__item')];

        if (!this.track || this.items.length === 0) return;

        this.isDragging = false;
        this.startX = 0;
        this.scrollStart = 0;
        this.hasMoved = false;

        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleTrackClick = this.handleTrackClick.bind(this);
        this.handleMouseEnter = this.pauseAutoplay.bind(this);
        this.handleMouseLeave = this.resumeAutoplay.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);

        this.prevBtn?.addEventListener('click', this.handlePrev);
        this.nextBtn?.addEventListener('click', this.handleNext);
        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.addEventListener('keydown', this.handleKeyDown);

        // Mouse drag scrolling support
        this.track.addEventListener('pointerdown', this.handlePointerDown);
        window.addEventListener('pointermove', this.handlePointerMove);
        window.addEventListener('pointerup', this.handlePointerUp);
        window.addEventListener('pointercancel', this.handlePointerUp);

        // Prevent unwanted card click when dragging
        this.track.addEventListener('click', this.handleTrackClick, true);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateControls());
          this.resizeObserver.observe(this.track);
        }

        document.addEventListener('shopify:section:load', this.handleSectionLoad);

        this.updateControls();
        this.setupAutoplay();
      }

      disconnectedCallback() {
        this.prevBtn?.removeEventListener('click', this.handlePrev);
        this.nextBtn?.removeEventListener('click', this.handleNext);
        this.track?.removeEventListener('scroll', this.handleScroll);
        this.removeEventListener('keydown', this.handleKeyDown);
        this.track?.removeEventListener('pointerdown', this.handlePointerDown);
        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerUp);
        window.removeEventListener('pointercancel', this.handlePointerUp);
        this.track?.removeEventListener('click', this.handleTrackClick, true);
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        this.resizeObserver?.disconnect();
        this.clearAutoplay();
        this.dataset.initialized = 'false';
      }

      handleSectionLoad(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) {
          this.updateControls();
        }
      }

      handleTrackClick(event) {
        if (!this.hasMoved) return;
        event.preventDefault();
        event.stopPropagation();
        this.hasMoved = false;
      }

      getStep() {
        if (!this.items.length) return this.track.clientWidth;
        const itemWidth = this.items[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(this.track).gap || 0);
        return itemWidth + gap;
      }

      handlePrev() {
        this.track.scrollBy({ left: -this.getStep(), behavior: 'smooth' });
      }

      handleNext() {
        this.track.scrollBy({ left: this.getStep(), behavior: 'smooth' });
      }

      handleScroll() {
        this.updateProgress();
        this.updateButtons();
      }

      handleKeyDown(e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.handlePrev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.handleNext();
        }
      }

      handlePointerDown(e) {
        if (e.pointerType === 'touch') return; // native touch scrolling
        if (e.button !== 0) return; // left click only

        this.isDragging = true;
        this.hasMoved = false;
        this.startX = e.clientX;
        this.scrollStart = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        this.pauseAutoplay();
      }

      handlePointerMove(e) {
        if (!this.isDragging) return;
        const delta = e.clientX - this.startX;
        if (Math.abs(delta) > 5) {
          this.hasMoved = true;
        }
        this.track.scrollLeft = this.scrollStart - delta;
      }

      handlePointerUp() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.track.classList.remove('is-dragging');
        this.resumeAutoplay();
      }

      updateControls() {
        this.updateProgress();
        this.updateButtons();
      }

      updateProgress() {
        if (!this.track) return;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const scrollFraction = maxScroll > 0 ? Math.min(Math.max(this.track.scrollLeft / maxScroll, 0), 1) : 0;

        if (this.progressBar) {
          const visibleRatio = Math.min(this.track.clientWidth / this.track.scrollWidth, 1);
          const thumbWidthPercent = Math.max(visibleRatio * 100, 15);
          const maxThumbTranslate = 100 - thumbWidthPercent;
          const currentTranslate = scrollFraction * maxThumbTranslate;

          this.progressBar.style.width = `${thumbWidthPercent}%`;
          this.progressBar.style.transform = `translateX(${currentTranslate * (100 / thumbWidthPercent)}%)`;
        }
      }

      updateButtons() {
        if (!this.track) return;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const hasOverflow = maxScroll > 2;

        if (this.prevBtn) {
          this.prevBtn.disabled = this.track.scrollLeft <= 2;
          this.prevBtn.hidden = !hasOverflow;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = this.track.scrollLeft >= maxScroll - 2;
          this.nextBtn.hidden = !hasOverflow;
        }
      }

      setupAutoplay() {
        const isAutoplay = this.dataset.autoplay === 'true';
        const speed = parseInt(this.dataset.autoplaySpeed || '5', 10) * 1000;

        if (!isAutoplay || speed < 2000) return;

        this.clearAutoplay();
        this.autoplayTimer = setInterval(() => {
          if (this.isPaused || this.isDragging) return;
          const maxScroll = this.track.scrollWidth - this.track.clientWidth;
          if (this.track.scrollLeft >= maxScroll - 5) {
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            this.handleNext();
          }
        }, speed);

        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }

      pauseAutoplay() {
        this.isPaused = true;
      }

      resumeAutoplay() {
        this.isPaused = false;
      }

      clearAutoplay() {
        if (this.autoplayTimer) {
          clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }
    }
  );
}
